import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Trash2, Coins, Vote, AlertCircle, TrendingUp, X, Mail, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  loan_request: { icon: Coins, color: 'text-blue-400 bg-blue-500/10' },
  bid_received: { icon: TrendingUp, color: 'text-green-400 bg-green-500/10' },
  bid_accepted: { icon: Check, color: 'text-emerald-400 bg-emerald-500/10' },
  bid_rejected: { icon: X, color: 'text-red-400 bg-red-500/10' },
  governance: { icon: Vote, color: 'text-purple-400 bg-purple-500/10' },
  system: { icon: AlertCircle, color: 'text-orange-400 bg-orange-500/10' },
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function NotificationDropdown() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState({ email_notifications: true, email_enabled: false });
  const dropdownRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/notifications/unread-count`, { headers });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count);
      }
    } catch {}
  }, [token]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/notifications`, { headers });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications);
      }
    } catch {} finally { setLoading(false); }
  }, [token]);

  // Poll for unread count
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when opened
  useEffect(() => {
    if (open && !showPrefs) fetchNotifications();
    if (open) fetchEmailPrefs();
  }, [open, fetchNotifications]);

  const fetchEmailPrefs = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/notifications/preferences`, { headers });
      if (res.ok) setEmailPrefs(await res.json());
    } catch {}
  };

  const toggleEmailNotifs = async () => {
    const newVal = !emailPrefs.email_notifications;
    setEmailPrefs(prev => ({ ...prev, email_notifications: newVal }));
    try {
      await fetch(`${API}/api/notifications/preferences`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_notifications: newVal })
      });
    } catch {}
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (id) => {
    await fetch(`${API}/api/notifications/${id}/read`, { method: 'PUT', headers });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/read-all`, { method: 'PUT', headers });
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const deleteNotif = async (id, wasUnread) => {
    await fetch(`${API}/api/notifications/${id}`, { method: 'DELETE', headers });
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    if (notif.link) { navigate(notif.link); setOpen(false); }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-12 w-96 max-h-[480px] bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50"
            data-testid="notification-dropdown"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
              <h3 className="text-sm font-semibold">{showPrefs ? 'Email Settings' : 'Notifications'}</h3>
              <div className="flex items-center gap-2">
                {!showPrefs && unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
                    data-testid="mark-all-read-btn"
                  >
                    <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                  </button>
                )}
                <button
                  onClick={() => setShowPrefs(!showPrefs)}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                  data-testid="notification-settings-btn"
                >
                  <Settings className="w-3.5 h-3.5 text-gray-500" />
                </button>
              </div>
            </div>

            {showPrefs ? (
              /* Email Preferences Panel */
              <div className="p-4 space-y-4" data-testid="email-preferences">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">Email Notifications</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {emailPrefs.email_enabled
                        ? 'Receive email alerts for platform activity'
                        : 'Email service not configured yet'}
                    </div>
                  </div>
                  <button
                    onClick={toggleEmailNotifs}
                    className={`relative w-10 h-5 rounded-full transition-colors ${emailPrefs.email_notifications ? 'bg-blue-500' : 'bg-gray-700'}`}
                    data-testid="email-toggle"
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${emailPrefs.email_notifications ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                
                {!emailPrefs.email_enabled && (
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <div className="flex items-center gap-2 text-xs text-yellow-400">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                      <span>Emails will be sent once a SendGrid API key is configured. Your preferences are saved.</span>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="text-xs text-gray-500 font-medium">Notify me about:</div>
                  {[
                    { key: 'loan_request', label: 'New loan requests', icon: Coins },
                    { key: 'bid_received', label: 'Bids on my loans', icon: TrendingUp },
                    { key: 'bid_accepted', label: 'Bid accepted/rejected', icon: Check },
                    { key: 'governance', label: 'Governance proposals', icon: Vote },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <item.icon className="w-3.5 h-3.5" />
                        {item.label}
                      </div>
                      <Mail className="w-3.5 h-3.5 text-blue-400" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
            /* Notification List */
            <div className="overflow-y-auto max-h-[400px]" data-testid="notification-list">
              {loading && notifications.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-500">Loading...</div>
              ) : notifications.length === 0 ? (
                <div className="py-12 text-center">
                  <Bell className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No notifications yet</p>
                  <p className="text-xs text-gray-600 mt-1">You'll be notified about loan activity and updates</p>
                </div>
              ) : (
                notifications.map((notif) => {
                  const config = ICON_MAP[notif.type] || ICON_MAP.system;
                  const Icon = config.icon;
                  return (
                    <div
                      key={notif.id}
                      className={`flex items-start gap-3 px-4 py-3 border-b border-white/[0.03] hover:bg-white/[0.03] transition-colors cursor-pointer group ${!notif.read ? 'bg-blue-500/[0.04]' : ''}`}
                      onClick={() => handleClick(notif)}
                      data-testid={`notification-item-${notif.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center shrink-0 mt-0.5`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium truncate ${!notif.read ? 'text-white' : 'text-gray-400'}`}>
                            {notif.title}
                          </span>
                          {!notif.read && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.message}</p>
                        <span className="text-[10px] text-gray-600 mt-1 block">{timeAgo(notif.created_at)}</span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteNotif(notif.id, !notif.read); }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-white/10 rounded transition-all shrink-0"
                        data-testid={`delete-notif-${notif.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-gray-500" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
